import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	CheckSquare,
	Calendar,
	Briefcase,
	Building2,
	User,
	Sparkles,
	FileText,
	Ticket,
	Folder,
	ClipboardList,
	Trash2,
	Repeat2,
	Layers,
	Filter
} from 'lucide-react';
import T from 'Components/Helpers/translate';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ClientResource from 'App/resources/Client';
import ContactResource from 'Resources/Contact';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import FieldGrid, { ENTITY_FIELDS } from './FieldGrid';
import { executionFailureMessage } from './approvalAdapters';
import type { MultiActionConfig, MultiActionFilter, MultiActionProperty } from './types';

type Props = {
	config: MultiActionConfig;
	onUpdate?: (id: string, updates: Partial<MultiActionConfig>) => void;
	defaultExpanded?: boolean;
	/** When true, the card renders in a fully inert state — no approve /
	 *  reject buttons, no field editors fire onChange. Used by the trace
	 *  drawer when displaying an already-resolved approval. */
	readOnly?: boolean;
};

const ENTITY_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
	activity: CheckSquare,
	todo: CheckSquare,
	task: CheckSquare,
	appointment: Calendar,
	meeting: Calendar,
	order: Briefcase,
	deal: Briefcase,
	opportunity: Briefcase,
	client: Building2,
	account: Building2,
	company: Building2,
	contact: User,
	person: User,
	lead: Sparkles,
	agreement: FileText,
	ticket: Ticket,
	project: Folder,
	projectplan: ClipboardList
};

const ENTITY_SINGULAR_KEY: Record<string, string> = {
	client: 'default.client',
	contact: 'default.contact',
	activity: 'default.activity',
	appointment: 'default.appointment',
	order: 'default.order',
	lead: 'default.lead',
	ticket: 'default.ticket'
};

const ENTITY_PLURAL_KEY: Record<string, string> = {
	client: 'default.accounts',
	contact: 'default.contacts',
	activity: 'default.activities',
	appointment: 'default.appointments',
	order: 'default.orders',
	lead: 'default.leads',
	ticket: 'default.tickets'
};

const TASK_LABEL_KEY: Record<string, string> = {
	Create: 'default.create',
	Update: 'default.update',
	Delete: 'default.delete'
};

const translate = (key: string, fallback: string): string => {
	const v = T(key);
	return v && v !== key ? v : fallback;
};

const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const labelForEntity = (entity: string, count: number): string => {
	const map = count === 1 ? ENTITY_SINGULAR_KEY : ENTITY_PLURAL_KEY;
	const key = map[entity.toLowerCase()];
	if (key) return translate(key, titleCase(entity));
	return titleCase(entity);
};

const labelForTask = (task: string): string => {
	const key = TASK_LABEL_KEY[task];
	return key ? translate(key, titleCase(task)) : titleCase(task);
};

function statusToPill(status: MultiActionConfig['status'], task: MultiActionConfig['task']): StatusPill | null {
	switch (status) {
		case 'executing':
			return { label: T('assistant.saving') || 'Saving…', tone: 'saving' };
		case 'completed': {
			const key =
				task === 'Delete'
					? 'assistant.actionDeleted'
					: task === 'Create'
					? 'assistant.actionCreated'
					: 'assistant.actionUpdated';
			return { label: T(key) || 'Done', tone: 'ok' };
		}
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'failed':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const propsToDataKey = (name: string): string => name; // Preserve original casing; FieldGrid handles case-insensitive ref detection.

/** properties[]  ↔  data{}  — FieldGrid takes a Record<string,unknown>. We
 *  map property entries into a data object keyed by the original property
 *  name (so save round-trips cleanly), preserving order. */
function propertiesToData(properties: MultiActionProperty[]): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const p of properties) {
		// Bare numeric ids for User / ActivityType / etc. wrap to {id} so
		// FieldGrid's hydration picks them up like the single-entity card.
		const lower = p.name.toLowerCase();
		const isRef = !!ENTITY_FIELDS[lower];
		out[propsToDataKey(p.name)] =
			isRef && (typeof p.value === 'number' || typeof p.value === 'string') ? { id: p.value } : p.value;
	}
	return out;
}

const COMPARISON_SYMBOLS: Record<string, string> = {
	eq: '=',
	ne: '≠',
	gt: '>',
	gte: '≥',
	lt: '<',
	lte: '≤',
	in: 'in',
	notIn: 'not in',
	wc: 'contains',
	contains: 'contains',
	startsWith: 'starts with',
	endsWith: 'ends with',
	exists: 'is set',
	notExists: 'is empty'
};

const formatFilterValue = (v: unknown): string => {
	if (v == null) return '—';
	if (Array.isArray(v)) {
		if (v.length === 0) return '[]';
		if (v.length > 2) return `[${v.length}]`;
		return v.map(x => formatFilterValue(x)).join(', ');
	}
	if (typeof v === 'object') {
		const o = v as { name?: unknown; id?: unknown };
		if (typeof o.name === 'string' && o.name) return o.name;
		if (typeof o.id === 'number' || typeof o.id === 'string') return `#${o.id}`;
		try {
			return JSON.stringify(v);
		} catch {
			return String(v);
		}
	}
	if (typeof v === 'string') return v.length > 40 ? `${v.slice(0, 37)}…` : v;
	return String(v);
};

const formatFilterChip = (f: MultiActionFilter): string => {
	const field = f.field || '?';
	const symbol = COMPARISON_SYMBOLS[f.comparison] ?? f.comparison ?? '=';
	if (f.comparison === 'exists' || f.comparison === 'notExists') {
		return `${field} ${symbol}`;
	}
	return `${field} ${symbol} ${formatFilterValue(f.value)}`;
};

function dataToProperties(data: Record<string, unknown>, originalOrder: MultiActionProperty[]): MultiActionProperty[] {
	const orderKeys = originalOrder.map(p => p.name);
	const result: MultiActionProperty[] = [];
	for (const key of orderKeys) {
		if (!(key in data)) continue;
		const raw = data[key];
		const lower = key.toLowerCase();
		const isRef = !!ENTITY_FIELDS[lower];
		// Server expects primitive ids for ref properties; unwrap {id} back to id.
		const value =
			isRef && raw && typeof raw === 'object' && 'id' in (raw as object) ? (raw as { id: unknown }).id : raw;
		result.push({ name: key, value });
	}
	// Any new keys the user added (shouldn't happen in this flow, but be safe).
	for (const [k, v] of Object.entries(data)) {
		if (orderKeys.includes(k)) continue;
		result.push({ name: k, value: v });
	}
	return result;
}

const MultiActionWriteCard = ({ config, onUpdate, defaultExpanded, readOnly = false }: Props) => {
	const isPending = config.status === 'pending';
	const isDelete = config.task === 'Delete';
	const resolved = config.status === 'completed' || config.status === 'cancelled';
	const isFailed = config.status === 'failed';

	const Icon = ENTITY_ICONS[config.entity.toLowerCase()] ?? Layers;

	const [editedData, setEditedData] = useState<Record<string, unknown>>(() => propertiesToData(config.properties));
	// Keep editedData in sync if the upstream config swaps (e.g. status change
	// from pending → executing → completed doesn't carry our edits, but a
	// fresh proposal would).
	useEffect(() => {
		setEditedData(propertiesToData(config.properties));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config.approvalId, config.id]);

	// Resolve parent record names so the loop-targets chips show "Acme Corp"
	// instead of bare ids. Same pattern as WriteCard's displayCache.
	const numericSelectedIds = useMemo(
		() =>
			(config.selectedIds || [])
				.map(v => (typeof v === 'number' ? v : Number(v)))
				.filter((n): n is number => Number.isFinite(n)),
		[config.selectedIds]
	);
	const [targetNames, setTargetNames] = useState<Map<number, string>>(() => new Map());
	useEffect(() => {
		if (!numericSelectedIds.length) return;
		const parent = (config.entityRoute || '').toLowerCase();
		// Old approvals don't have entityRoute set on the payload — fall back
		// to Client lookup, which is the parent the vast majority of multi-
		// actions iterate. Worst case: the lookup misses and chips fall back
		// to bare ids (current behaviour).
		const resource =
			parent === 'contact'
				? ContactResource
				: parent === 'client' || parent === 'account' || parent === 'company' || parent === ''
				? ClientResource
				: null;
		if (!resource) return;
		let cancelled = false;
		(async () => {
			try {
				const res = await (
					resource as unknown as {
						find: (p: Record<string, unknown>) => Promise<{ data: Array<Record<string, unknown>> }>;
					}
				).find({
					limit: numericSelectedIds.length,
					q: [{ a: 'id', c: 'eq', v: numericSelectedIds }]
				});
				if (cancelled) return;
				const map = new Map<number, string>();
				for (const row of (res?.data ?? []) as Array<{
					id: number;
					name?: string;
					firstName?: string;
					lastName?: string;
				}>) {
					const name = row.name || [row.firstName, row.lastName].filter(Boolean).join(' ');
					if (name) map.set(row.id, name);
				}
				setTargetNames(map);
			} catch {
				/* fall back to bare ids */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [numericSelectedIds, config.entityRoute]);

	const targetLabel = useMemo(() => {
		const count = config.selectedIds?.length ?? 0;
		// Default the label to "Client" when entityRoute isn't set — the same
		// resource we fall back to for the name lookup above. Keeps the chips
		// and the section label consistent.
		const parentKey = ((config.entityRoute || 'client') as string).toLowerCase();
		const map = count === 1 ? ENTITY_SINGULAR_KEY : ENTITY_PLURAL_KEY;
		const langKey = map[parentKey];
		const parentLabel = langKey ? translate(langKey, titleCase(parentKey)) : titleCase(parentKey);
		return `${parentLabel} ${T('default.ids') || 'IDs'} (${count})`;
	}, [config.selectedIds, config.entityRoute]);

	// Filter-based targeting: rendered only when no explicit selectedIds (which
	// take precedence in the executor). Shows the filters the agent matched on
	// plus an "≈ N {parent}" estimate so the rep can see scope before approving.
	const showFilterTargets = useMemo(() => {
		const hasFilters = (config.filters?.length ?? 0) > 0;
		const hasIds = (config.selectedIds?.length ?? 0) > 0;
		return hasFilters && !hasIds;
	}, [config.filters, config.selectedIds]);

	const filterTargetLabel = useMemo(() => {
		if (!showFilterTargets) return '';
		const count = config.estimatedCount ?? 0;
		const parentKey = ((config.entityRoute || 'client') as string).toLowerCase();
		const map = count === 1 ? ENTITY_SINGULAR_KEY : ENTITY_PLURAL_KEY;
		const langKey = map[parentKey];
		const parentLabel = langKey ? translate(langKey, titleCase(parentKey)) : titleCase(parentKey);
		const appliesTo = T('assistant.appliesTo') || 'Applies to';
		if (!count) return `${appliesTo} ${parentLabel}`;
		return `${appliesTo} ${parentLabel} · ≈ ${count}`;
	}, [showFilterTargets, config.estimatedCount, config.entityRoute]);

	const handleConfirm = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate(config.id, { status: 'executing' });
		try {
			const properties = dataToProperties(editedData, config.properties);
			let message: string;
			if (config.approvalId) {
				// Build modifiedPayload from fields we actually have values for.
				// The server's validateEditedPayload throws if `entity` / `action`
				// are sent but differ from the original — and `action` is missing
				// from approvals created via taskRunner (which stores the raw AI
				// tool input, not the registry-derived action name). The executor
				// re-derives `actionName` from task+entity anyway, so omitting it
				// is harmless.
				const modifiedPayload: Record<string, unknown> = {
					task: config.task,
					entityRoute: config.entityRoute,
					filters: config.filters,
					properties,
					selectedIds: config.selectedIds,
					estimatedCount: config.estimatedCount,
					description: config.description
				};
				if (config.entity) modifiedPayload.entity = config.entity;
				if (config.action) modifiedPayload.action = config.action;
				const { approval } = await AgentApprovalsResource.approve(
					config.approvalId,
					undefined,
					modifiedPayload
				);
				const resultPayload = approval.resultPayload as {
					totalChanged?: number | null;
					total?: number | null;
				} | null;
				const failure = executionFailureMessage(
					approval.resultPayload as Record<string, unknown> | null | undefined
				);
				if (failure !== null) {
					onUpdate(config.id, {
						status: 'failed',
						resultMessage: failure || T('default.failed') || 'Execution failed'
					});
					return;
				}
				const totalChanged = resultPayload?.totalChanged;
				const actionPast =
					config.task === 'Delete'
						? T('assistant.actionDeleted')
						: config.task === 'Create'
						? T('assistant.actionCreated')
						: T('assistant.actionUpdated');
				message =
					totalChanged != null
						? `${actionPast} ${totalChanged} ${config.entity.toLowerCase()}.`
						: `${labelForTask(config.task)} — ${config.estimatedCount} ${config.entity.toLowerCase()}.`;
			} else {
				message = `${labelForTask(config.task)} — ${config.estimatedCount} ${config.entity.toLowerCase()}.`;
			}
			onUpdate(config.id, { status: 'completed', resultMessage: message });
		} catch (err) {
			onUpdate(config.id, {
				status: 'failed',
				resultMessage: err instanceof Error ? err.message : 'Unknown error'
			});
		}
	}, [config, editedData, onUpdate]);

	const handleCancel = useCallback(async () => {
		if (config.approvalId) {
			try {
				await AgentApprovalsResource.reject(config.approvalId, 'cancelled_in_chat');
			} catch {
				/* non-fatal */
			}
		}
		onUpdate?.(config.id, { status: 'cancelled' });
	}, [config.approvalId, config.id, onUpdate]);

	// A failed approval row is terminal server-side, so re-arming the card locally
	// just sends the next click into the resolve guard's `approval_rejected`. The
	// retry endpoint re-executes a resolved row (WEB-9873).
	const handleRetry = useCallback(async () => {
		if (!onUpdate) return;
		if (!config.approvalId) {
			onUpdate(config.id, { status: 'pending', resultMessage: undefined });
			return;
		}
		onUpdate(config.id, { status: 'executing', resultMessage: undefined });
		try {
			const { approval } = await AgentApprovalsResource.retry(config.approvalId);
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			if (failure !== null) {
				onUpdate(config.id, {
					status: 'failed',
					resultMessage: failure || T('default.failed') || 'Execution failed'
				});
				return;
			}
			const totalChanged = (approval.resultPayload as { totalChanged?: number | null } | null)?.totalChanged;
			onUpdate(config.id, {
				status: 'completed',
				resultMessage:
					totalChanged != null
						? `${
								config.task === 'Delete'
									? T('assistant.actionDeleted')
									: config.task === 'Create'
									? T('assistant.actionCreated')
									: T('assistant.actionUpdated')
						  } ${totalChanged} ${config.entity.toLowerCase()}.`
						: `${labelForTask(config.task)} — ${config.estimatedCount} ${config.entity.toLowerCase()}.`
			});
		} catch (err) {
			onUpdate(config.id, {
				status: 'failed',
				resultMessage: err instanceof Error ? err.message : 'Retry failed'
			});
		}
	}, [config.approvalId, config.id, config.task, config.entity, config.estimatedCount, onUpdate]);

	const title = `${labelForTask(config.task)} ${labelForEntity(config.entity, config.estimatedCount)}${
		config.estimatedCount > 0 ? ` (${config.estimatedCount})` : ''
	}`;
	const chips: string[] = [];
	if (config.createdDate) chips.push(config.createdDate);
	if (config.description) chips.push(config.description);
	const statusPill = statusToPill(config.status, config.task);
	const approveLabel = isFailed
		? T('assistant.retry')
		: `${labelForTask(config.task)}${config.estimatedCount > 0 ? ` (${config.estimatedCount})` : ''}`;

	const body = (
		<div className="pr-bulk-body">
			{config.status === 'failed' && config.resultMessage && (
				<div
					style={{
						marginBottom: 10,
						padding: '8px 10px',
						borderRadius: 4,
						background: 'rgba(178,52,52,0.08)',
						color: 'var(--ws-err, #b23434)',
						fontSize: 12.5
					}}
				>
					{config.resultMessage}
				</div>
			)}
			{config.status === 'completed' && config.resultMessage && (
				<div
					style={{
						marginBottom: 10,
						padding: '8px 10px',
						borderRadius: 4,
						background: 'rgba(47,122,78,0.08)',
						color: 'var(--ws-ok, #2f7a4e)',
						fontSize: 12.5
					}}
				>
					{config.resultMessage}
				</div>
			)}

			{showFilterTargets && (
				<div className="ws-trace-preview-targets">
					<div className="ws-trace-preview-targets-head">
						<Filter className="ws-trace-preview-targets-icon" />
						<span className="ws-trace-preview-targets-label">{filterTargetLabel}</span>
					</div>
					<div className="ws-trace-preview-targets-ids">
						{(config.filters || []).map((f, i) => (
							<span
								key={`${f.field}-${f.comparison}-${i}`}
								className="ws-trace-preview-target-chip"
								title={formatFilterChip(f)}
							>
								{formatFilterChip(f)}
							</span>
						))}
					</div>
				</div>
			)}

			{(config.selectedIds?.length ?? 0) > 0 && (
				<div className="ws-trace-preview-targets" title={(config.selectedIds || []).join(', ')}>
					<div className="ws-trace-preview-targets-head">
						<Repeat2 className="ws-trace-preview-targets-icon" />
						<span className="ws-trace-preview-targets-label">{targetLabel}</span>
					</div>
					<div className="ws-trace-preview-targets-ids">
						{((config.selectedIds || []).length <= 12
							? config.selectedIds || []
							: (config.selectedIds || []).slice(0, 12)
						).map((id, i) => {
							const numericId = typeof id === 'number' ? id : Number(id);
							const name = Number.isFinite(numericId) ? targetNames.get(numericId) : undefined;
							return (
								<span
									key={`${String(id)}-${i}`}
									className="ws-trace-preview-target-chip"
									title={name ? `${name} (#${String(id)})` : `#${String(id)}`}
								>
									{name ? (
										<>
											<span className="ws-trace-preview-target-chip-name">{name}</span>
											<span className="ws-trace-preview-target-chip-id">{`#${String(id)}`}</span>
										</>
									) : (
										<>{`#${String(id)}`}</>
									)}
								</span>
							);
						})}
						{(config.selectedIds || []).length > 12 && (
							<span className="ws-trace-preview-target-chip ws-trace-preview-target-chip--more">
								{'+'}
								{(config.selectedIds || []).length - 12}
							</span>
						)}
					</div>
				</div>
			)}

			{!isDelete && <FieldGrid data={editedData} onChange={setEditedData} disabled={readOnly || !isPending} />}
		</div>
	);

	return (
		<ProposalCardShell
			icon={isDelete ? Trash2 : Icon}
			title={title}
			chips={chips}
			statusPill={statusPill}
			body={body}
			busy={config.status === 'executing'}
			resolved={resolved}
			approveLabel={approveLabel}
			rejectLabel={T('assistant.cancel')}
			onApprove={readOnly ? undefined : isFailed ? handleRetry : isPending ? handleConfirm : undefined}
			onReject={readOnly ? undefined : isPending ? handleCancel : undefined}
			defaultExpanded={defaultExpanded}
		/>
	);
};

export default MultiActionWriteCard;
